import React from "react"

import { DEFAULT_LANGUAGE, useTranslation } from "@bounce/i18n"
import { useReferrals } from "@bounce/referrals"
import type { Nullable } from "@bounce/util"
import { useIsMobile } from "@bounce/web-components"

import { Cities } from "./components/Cities/Cities"
import { Hero } from "./components/Hero"
import { useSEO } from "./hooks/useSEO"
import { ReviewsSlider } from "@/components/Generic/Reviews/ReviewsSlider/ReviewsSlider"
import { SearchboxStructuredData } from "@/components/Generic/StructuredData/StructuredData"
import DynamicAppBannerSecondary from "@/components/LuggageStorage/AppBanner/DynamicAppBannerSecondary"
import { FAQs } from "@/components/LuggageStorage/FAQs/FAQs"
import { HowItWorks } from "@/components/LuggageStorage/HowItWorks/HowItWorks"
import { Layout } from "@/components/LuggageStorage/Layout/Layout"
import DynamicNewsletterBanner from "@/components/LuggageStorage/NewsletterBanner/DynamicNewsletterBanner"
import { Pricing } from "@/components/LuggageStorage/Pricing/Pricing"
import StoreSliderSection from "@/components/LuggageStorage/StoreSlider/StoreSliderSection"
import { WhyUseBounce } from "@/components/LuggageStorage/WhyUseBounce/WhyUseBounce"
import { config } from "@/config"
import { Feature } from "@/config/flags"
import { useSSRExperiment } from "@/contexts/SSRExperiments"
import { StickyCTAProvider } from "@/contexts/StickyCTAContext"
import { useGenericInterpolations } from "@/hooks/useGenericInterpolations"
import type { Country } from "@/libs/bounce/bounceSdk.types.gen"
import type { CityLink } from "@/libs/bounce/cities"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"
import type { AggregateRating } from "@/libs/bounce/types"
import type { FormattedHomepage } from "@/libs/prismic/homepage/types"
import { useHomeUrl } from "@/libs/router"
import { HOMEPAGE_ONLY_LANGUAGES } from "@/utils/i18n/locale"

export type HomeScreenProps = {
  homePage: Nullable<FormattedHomepage>
  cities: CityLink[]
  countries: Country[]
  aggregateRating: AggregateRating
  country: string
  stores: NearbyStorageSpot[]
  searchQuery: string
}

const LANGUAGES = Object.values(config.locales)
  .filter(locale => locale.active || HOMEPAGE_ONLY_LANGUAGES.includes(locale.language))
  .map(locale => locale.language)

const HomeScreen = ({
  homePage,
  cities,
  countries,
  aggregateRating,
  country,
  stores,
  searchQuery,
}: HomeScreenProps) => {
  const { absolutePath, getUrl } = useHomeUrl()
  const interpolations = useGenericInterpolations({ countries, geoIpCountry: country })
  const { metaTitle, metaDescription } = useSEO({ homePage, interpolations })
  const isMobile = useIsMobile()
  const { t } = useTranslation("marketing-ls-home")

  const { coupon } = useReferrals.useCodes()
  const alternates = LANGUAGES.map(language => ({
    language,
    href: getUrl({ absolute: true, language }),
  })).concat([{ language: "x-default", href: getUrl({ absolute: true, language: DEFAULT_LANGUAGE }) }])

  const { isEnabled } = useSSRExperiment({
    flagKey: Feature.AddStoreSliderToHomepage,
  })

  return (
    <StickyCTAProvider config={{ from: "Home", coupon }}>
      <Layout
        headProps={{
          title: metaTitle,
          description: metaDescription,
          url: absolutePath,
        }}
        from="Home"
        languages={LANGUAGES}
        hideNewsletterBanner
        alternates={alternates}>
        <Hero interpolations={interpolations} aggregateRating={aggregateRating} />
        {isEnabled && stores.length > 0 && (
          <StoreSliderSection
            name="HomeNearestStores"
            from="Home"
            searchQuery={searchQuery}
            lowestPrice={interpolations.LOWEST_PRICE}
            path={absolutePath}
            stores={stores}
            title={t(
              "screen.homeScreen.nearbyStoresSlider.title",
              "Places to store luggage around your location",
              interpolations,
            )}
            subtitle={t(
              "screen.homeScreen.nearbyStoresSlider.subtitle",
              "Keep your bags safe at one of our convenient luggage storage spots",
              interpolations,
            )}
            sectionViewedMeta={{ section: "HomeNearestStores" }}
          />
        )}
        <ReviewsSlider name="ReviewsSliderHome" className={isEnabled ? "py-section" : "pb-section"} />
        <HowItWorks variant={isMobile ? "app-download" : "find-closest-location"} from="Home" />
        <DynamicAppBannerSecondary variant="find-closest-location" titleClassName="md:mt-16" />
        <WhyUseBounce className="py-section" currency={interpolations.CURRENCY} />
        <Pricing
          countries={countries}
          currency={interpolations.CURRENCY}
          defaultCountry={interpolations.COUNTRY_CODE}
        />
        <DynamicNewsletterBanner wrapperClass="w-full pt-section" />
        <FAQs className="pt-section" />
        <Cities cities={cities} />
        <SearchboxStructuredData />
      </Layout>
    </StickyCTAProvider>
  )
}

export default HomeScreen
